/**
 * Pure resolution of "who is this login?" — answered as TWO questions, in order:
 *
 *   1. SELECTION  — which AP_Master row is this login? (`selectCurrentAPRecord`)
 *   2. ACCEPTANCE — does that row buy a working client/shift/document context?
 *                   (`resolveAPIdentity`, via `accessGranted`)
 *
 * They are deliberately separate answers from one call. An `Inactive` AP must
 * still RESOLVE — `APShell.tsx:34` routes them to `ArchivedAPPage` with
 * compliance-upload access, and that self-remediation experience is the correct,
 * deliberate design — but resolving must not hand them shifts, client profiles
 * or client documents. Collapse the two into one function and the archived
 * experience breaks; merge them the other way and offboarding stops revoking.
 *
 * `api/shared/apIdentity.js` is the CommonJS mirror of this rule for the API
 * (which cannot import from `src/`). Neither copy is the source of truth on its
 * own: both are held to `api/shared/apIdentity.cases.json`, a corpus of real
 * observed row shapes that each runtime executes and must answer identically.
 * If you change a rule here, add the case there first.
 *
 * ---------------------------------------------------------------------------
 * Why selection is status-first rather than list order:
 *
 * AP_Master does not enforce uniqueness on Email, and a single person legitimately
 * accumulates rows over time: an AP is archived (Status=Inactive) and later
 * re-engaged, and re-onboarding writes a NEW row rather than reviving the old one.
 * Test/pilot rows pile up against the same address too.
 *
 * The original lookup took the FIRST email match in list order, which is SharePoint
 * item order — effectively "oldest row wins". A re-onboarded AP would therefore
 * resolve to their stale Inactive row and be stranded on the archived experience
 * with no route back, even though their live row said Active. Observed 2026-08-04:
 * watt11andy@gmail.com held three rows (ZZTest_AP_Andy Inactive, A-001 Inactive,
 * ZA-001 Active) and the freshly provisioned AP was shown "account inactive".
 *
 * Precedence is therefore explicit and status-first: a live row always beats a dead
 * one, regardless of row order.
 *
 *   Active > Onboarding > Inactive
 *
 * Ties inside a status band are broken by the HIGHEST numeric list id — the most
 * recently created row — so a re-onboard beats an older row of the same status.
 * Archived APs still resolve (to their Inactive row) when that is all they have, so
 * the self-remediation experience is preserved.
 */

import type { APMasterItem, APStatus } from './apMasterService';

/** Lower rank wins. Anything unrecognised sorts last but is still selectable. */
export const STATUS_RANK: Record<APStatus, number> = {
  Active: 0,
  Onboarding: 1,
  Inactive: 2,
};

/**
 * The statuses that buy a data context. Exported so the backend mirror and any
 * gate reference ONE list rather than each hard-coding the literal `'Active'`.
 *
 * `Onboarding` is not on it: provisioning flips a row to `Active`, so a login
 * that can already sign in while still `Onboarding` has not been provisioned.
 * `Inactive` is not on it: that is ISS-030 — marking a provider Inactive reads
 * as a revocation and must behave as one on the data, while still leaving them
 * the archived compliance experience.
 */
export const AP_ACCESS_STATUSES: readonly APStatus[] = ['Active'];

function rankOf(status: APStatus | undefined): number {
  if (status && status in STATUS_RANK) return STATUS_RANK[status];
  return Number.MAX_SAFE_INTEGER;
}

/** Numeric list id for recency tie-breaks; non-numeric/absent ids sort oldest. */
function idOf(item: APMasterItem): number {
  const n = Number((item as { id?: string }).id);
  return Number.isFinite(n) ? n : -1;
}

/**
 * Pick the AP_Master row for a login.
 *
 * @param items    every AP_Master row read for this user
 * @param matches  predicate identifying rows belonging to this login (identity
 *                 matching — plain email, #ext# UPN, de-mangled — stays in the hook
 *                 so this module holds no auth knowledge)
 * @returns the winning row, or null when the login owns none
 */
export function selectCurrentAPRecord(
  items: APMasterItem[],
  matches: (item: APMasterItem) => boolean,
): APMasterItem | null {
  const owned = (items ?? []).filter(matches);
  if (owned.length === 0) return null;

  return owned.reduce((best, candidate) => {
    const byStatus = rankOf(candidate.Status) - rankOf(best.Status);
    if (byStatus !== 0) return byStatus < 0 ? candidate : best;
    return idOf(candidate) > idOf(best) ? candidate : best;
  });
}

// ---------------------------------------------------------------------------
// Acceptance — the second answer
// ---------------------------------------------------------------------------

/**
 * Why a resolution did or did not buy a data context. Always named, never a
 * bare `null` — a login that resolves to nothing must be able to say so on the
 * screen and in the log, rather than producing a blank app with a green tick.
 */
export type APIdentityOutcome = 'active' | 'not-active' | 'no-match' | 'no-apcode';

/** A PII-free descriptor of one owned row: safe to log, safe to show an operator. */
export interface APIdentityCandidate {
  id: string;
  apCode: string;
  status: string;
}

export interface APIdentityResolution {
  /** The selected row, or null when the login owns none. Present even when the
   *  row is refused (`not-active` / `no-apcode`) so diagnostics can name it. */
  record: APMasterItem | null;
  /** The selected row's trimmed APCode; null on `no-match` and on `no-apcode`. */
  apCode: string | null;
  outcome: APIdentityOutcome;
  /** True ONLY for `outcome === 'active'`. The single question every gate asks. */
  accessGranted: boolean;
  /** More than one owned row shares the winning status band — resolved, but reportable. */
  ambiguous: boolean;
  /** PII-free descriptors of every owned row, winner first. Never an email. */
  candidates: APIdentityCandidate[];
}

/**
 * Resolve a login to an AP_Master row AND decide whether that row grants a data
 * context — selection and acceptance, two answers from one call.
 *
 * Selection is DELEGATED to `selectCurrentAPRecord`, never reimplemented, so
 * the precedence rule lives in exactly one place and its 11 existing tests keep
 * guarding it.
 *
 * Acceptance, in order:
 *   - no owned row                    → `no-match`,  no context
 *   - owned row with a blank APCode   → `no-apcode`, no context
 *   - owned row whose Status is not
 *     in AP_ACCESS_STATUSES           → `not-active`, no context (it still resolves)
 *   - otherwise                       → `active`,    context granted
 *
 * The blank-APCode refusal comes FIRST because it is not an access decision a
 * caller should have to remember to make. `api/shared/ap.js:200/213/232` compare
 * `String(item.APID) === String(ctx.apCode)`; with a blank APCode that is
 * `'undefined' === 'undefined'`, which matches every row missing an `APID`
 * (ISS-025). Refusing it at the source is cheaper than remembering to guard
 * every comparison downstream.
 *
 * Ambiguity RESOLVES; it never blocks. Blocking would take a live AP down the
 * moment a second row appeared against their address — which is the
 * ISS-027/ISS-032 trap arriving from the other direction. It is reported so
 * somebody is told, and the answer stays deterministic.
 *
 * @param items    every AP_Master row read for this user
 * @param matches  predicate identifying rows belonging to this login. Identity
 *                 matching stays with the caller (the hook on the frontend,
 *                 `resolveContext` on the backend) so this module holds no auth
 *                 knowledge — but the corpus pins matching too, because the
 *                 ISS-027 semicolon behaviour is contract, not detail.
 */
export function resolveAPIdentity(
  items: APMasterItem[],
  matches: (item: APMasterItem) => boolean,
): APIdentityResolution {
  const owned = (items ?? []).filter(matches);

  // Winner first, then the rest in the same precedence order, so a logged or
  // rendered candidate list reads the same however SharePoint returned the rows.
  const candidates: APIdentityCandidate[] = [...owned]
    .sort((a, b) => rankOf(a.Status) - rankOf(b.Status) || idOf(b) - idOf(a))
    .map((item) => ({
      id: String((item as { id?: string }).id ?? ''),
      apCode: (item.APCode ?? '').trim(),
      status: String(item.Status ?? ''),
    }));

  const record = selectCurrentAPRecord(items, matches);
  if (!record) {
    return {
      record: null,
      apCode: null,
      outcome: 'no-match',
      accessGranted: false,
      ambiguous: false,
      candidates,
    };
  }

  // Ambiguity is measured against the WINNING band only. Two stale Inactive
  // rows sitting under one Active row is the ISS-032 shape — the Active answer
  // is unambiguous, and reporting it otherwise would be noise.
  const winningRank = rankOf(record.Status);
  const ambiguous = owned.filter((r) => rankOf(r.Status) === winningRank).length > 1;

  const apCode = (record.APCode ?? '').trim();
  if (apCode === '') {
    return { record, apCode: null, outcome: 'no-apcode', accessGranted: false, ambiguous, candidates };
  }

  if (!AP_ACCESS_STATUSES.includes(record.Status)) {
    return { record, apCode, outcome: 'not-active', accessGranted: false, ambiguous, candidates };
  }

  return { record, apCode, outcome: 'active', accessGranted: true, ambiguous, candidates };
}

// ---------------------------------------------------------------------------
// The same four outcomes, arriving as a refusal instead of as rows
// ---------------------------------------------------------------------------

/**
 * The named reasons `api/shared/ap.js` sends in `{ error: { message, code } }`.
 *
 * Mirrored here — not imported — because the API is a separate CommonJS bundle
 * the browser build cannot reach. Kept beside the outcomes it maps to so the
 * two lists are read together; `outcomeFromAPErrorCode` is the only place that
 * relates them, and `api/shared/ap.js:53` is the other copy.
 */
export const AP_IDENTITY_ERROR_CODES = {
  noMatch: 'AP_IDENTITY_NO_MATCH',
  noApCode: 'AP_IDENTITY_NO_APCODE',
  inactive: 'AP_INACTIVE',
  filterMismatch: 'AP_IDENTITY_FILTER_MISMATCH',
} as const;

/**
 * Turn a backend refusal code into the outcome it means.
 *
 * A caller who owns no row — or a row with no APCode — never receives the rows
 * to resolve: 35-02 refuses the `apMaster` read itself with a 403. So the SAME
 * four outcomes reach the browser by two different roads, and without this the
 * UI would need one switch for the rows it got and a second for the refusal it
 * got instead. That is exactly the split that let a blank screen mean four
 * different things on 2026-09-02.
 *
 * `AP_IDENTITY_FILTER_MISMATCH` deliberately maps to null: it is a composition
 * fault on one request, not a verdict about who the caller is, and dressing it
 * up as an identity outcome would put the wrong words on the screen.
 *
 * @returns the outcome, or null when the code names no identity verdict
 *          (unknown code, empty string, a plain network failure).
 */
export function outcomeFromAPErrorCode(
  code: string | null | undefined,
): APIdentityOutcome | null {
  switch (code) {
    case AP_IDENTITY_ERROR_CODES.noMatch:
      return 'no-match';
    case AP_IDENTITY_ERROR_CODES.noApCode:
      return 'no-apcode';
    case AP_IDENTITY_ERROR_CODES.inactive:
      return 'not-active';
    default:
      return null;
  }
}
